import { API_ROOT } from '../../supplementary_components/requests';
import { addNotification } from '../../supplementary_components/store';
import { dateFormatter, isValidEmailAddress, isValidPhoneNumber, phoneNumberTyping } from '../../supplementary_components/supplementaryMethods';
import { localDateToUSFormat } from '../../general_components/content_management/item_page/driver_page_components/payoutAdjustmentsSection';
import { dateStringToLocalDateString } from '../reports/weeklyReports';
import { hiringApi, hiringErrorMessage } from './hiringApi';

function paymentTypeValue(value) {
    if (value === 'Per Mile') return 'PerMile';
    if (value === 'Percentage' || value === 'Salary') return value;
    return null;
}

function wholeNumber(value) {
    if (value == null || value === '') return null;
    const number = Number(String(value).trim());
    if (!Number.isInteger(number)) return null;
    return number;
}

export function rollExperienceMonths(yearsValue, monthsValue) {
    const months = Number(String(monthsValue ?? '').trim());
    if (!Number.isInteger(months) || months < 12) return null;
    const years = Number(String(yearsValue ?? '').trim());
    const currentYears = Number.isInteger(years) && years > 0 ? years : 0;
    return {
        years: currentYears + Math.floor(months / 12),
        months: months % 12,
    };
}

export function defaultDriverType(rows) {
    const counts = {};
    (rows || []).forEach((row) => {
        if (!row?.driverType) return;
        counts[row.driverType] = (counts[row.driverType] || 0) + 1;
    });
    let best = null;
    let bestCount = 0;
    Object.entries(counts).forEach(([type, count]) => {
        if (count > bestCount) {
            best = type;
            bestCount = count;
        }
    });
    return best || 'Company';
}

function paymentTypeLabel(value) {
    if (value === 'PerMile') return 'Per Mile';
    if (value === 'Percentage' || value === 'Salary') return value;
    return '';
}

function accessibleCompanies() {
    try {
        const parsed = JSON.parse(localStorage.getItem('accessibleCompanies') || '[]');
        return Array.isArray(parsed) ? parsed : [];
    } catch (_) {
        return [];
    }
}

function partnerChoices(record, rows) {
    const choices = (rows || []).filter((row) => (
        row.id !== record.id && (!row.teamId || row.teamId === record.teamId)
    ));
    if (record.partnerId && !choices.some((row) => row.id === record.partnerId)) {
        choices.push({ id: record.partnerId, name: record.partnerName || `Applicant #${record.partnerId}` });
    }
    return choices;
}

function editOnlyColumns(record, rows) {
    const companies = accessibleCompanies();
    const columns = [
        {
            label: 'Coming Date',
            name: 'comingDate',
            autoComplete: 'off',
            calendarPickType: 'singleDate',
            defaultValue: () => (record.comingDate ? localDateToUSFormat(record.comingDate) : ''),
            checks: [
                (entered) => {
                    if (entered === '' || entered == null) return null;
                    return dateFormatter(entered).date === null ? 'Invalid Input' : null;
                },
            ],
            onblur: (entered) => dateFormatter(entered).date || entered,
            processBeforeSending: (entered) => (entered ? dateStringToLocalDateString(entered) : null),
        },
        {
            label: 'Status',
            name: 'status',
            type: 'select',
            dataset: [
                { value: 'NEW', label: 'New' },
                { value: 'PRE_HIRE_SENT', label: 'Pre-Hire Sent' },
                { value: 'PRE_HIRE_COMPLETED', label: 'Pre-Hire Completed' },
                { value: 'HIRED', label: 'Hired' },
                { value: 'REJECTED', label: 'Rejected' },
            ],
            defaultValue: () => record.status || 'NEW',
        },
        {
            label: 'Insurance Status',
            name: 'insuranceStatus',
            type: 'select',
            dataset: [
                { value: 'NOT_SENT', label: 'Not Sent' },
                { value: 'SENT', label: 'Sent' },
                { value: 'APPROVED', label: 'Approved' },
                { value: 'REJECTED', label: 'Rejected' },
            ],
            defaultValue: () => record.insuranceStatus || 'NOT_SENT',
        },
    ];
    if (companies.length > 1) {
        columns.push({
            label: 'Target Company',
            name: 'companyId',
            type: 'select',
            dataset: [
                { value: '', label: 'Not selected' },
                ...companies.map((company) => ({
                    value: String(company.id),
                    label: company.companyName || company.name || `Company #${company.id}`,
                })),
            ],
            defaultValue: () => (record.companyId == null ? '' : String(record.companyId)),
        });
    }
    columns.push({
        label: 'Co-driver',
        name: 'partnerId',
        type: 'select',
        dataset: [
            { value: '', label: 'None' },
            ...partnerChoices(record, rows).map((row) => ({
                value: String(row.id),
                label: row.name || `Applicant #${row.id}`,
            })),
        ],
        defaultValue: () => (record.partnerId == null ? '' : String(record.partnerId)),
    });
    return columns;
}

export function buildApplicantForm(rows, onSaved, record = null, presetPhone = '') {
    const conf = {
        formTitle: record ? 'Edit Applicant' : 'Add Applicant',
        name: record ? 'editApplicantForm' : 'addApplicantForm',
        addLink: record ? `${API_ROOT}potential-drivers/${record.id}` : `${API_ROOT}potential-drivers`,
        successMessage: record ? 'Applicant was updated' : 'Applicant was added',
        customSubmitButton: record ? 'Save' : 'Add',
        columns: [
            {
                label: 'Type',
                name: 'driverType',
                type: 'select',
                dataset: ['Company', 'Lease', 'Owner'],
                defaultValue: () => record?.driverType || defaultDriverType(rows),
                subMenu: {
                    title: 'Set Payment',
                    columnTemplate: '1fr 1fr',
                    noNeedToStringify: true,
                    columns: [
                        {
                            name: 'paymentType',
                            type: 'select',
                            dataset: [
                                { value: '', label: '-' },
                                { value: 'Per Mile', label: 'Per Mile' },
                                { value: 'Percentage', label: 'Percentage' },
                                { value: 'Salary', label: 'Salary' },
                            ],
                            isIndependentColumn: true,
                            defaultValue: () => paymentTypeLabel(record?.paymentType),
                            format: paymentTypeValue,
                        },
                        {
                            name: 'paymentAmount',
                            placeholder: 'Amount',
                            isIndependentColumn: true,
                            defaultValue: () => (record?.paymentAmount == null ? '' : String(record.paymentAmount)),
                            format: (value) => {
                                const number = Number(String(value).replace(/[^0-9.]/g, ''));
                                return Number.isFinite(number) ? number : null;
                            },
                        },
                    ],
                },
            },
            {
                label: 'Name',
                name: 'name',
                disableBrowserSuggestions: true,
                defaultValue: () => record?.name || '',
                subMenu: {
                    title: 'Add Reference',
                    columnTemplate: '1fr',
                    noNeedToStringify: true,
                    columns: [
                        {
                            name: 'reference',
                            placeholder: 'Reference',
                            disableBrowserSuggestions: true,
                            isIndependentColumn: true,
                            defaultValue: () => record?.reference || '',
                        },
                    ],
                },
            },
            {
                label: 'Phone',
                name: 'phoneNumber',
                disableBrowserSuggestions: true,
                defaultValue: () => record?.phoneNumber || presetPhone || '',
                filters: [phoneNumberTyping],
                onblur: (value, target) => (value ? isValidPhoneNumber(value, target) : ''),
            },
            {
                label: 'Email',
                name: 'email',
                disableBrowserSuggestions: true,
                defaultValue: () => record?.email || '',
                onblur: (value, target) => (value ? isValidEmailAddress(value, target) : ''),
            },
            {
                label: 'Experience',
                name: 'experience',
                type: 'fieldRow',
                fields: [
                    { name: 'experienceYears', placeholder: 'years', format: wholeNumber, disableBrowserSuggestions: true, defaultValue: () => (record?.experienceYears == null ? '' : String(record.experienceYears)) },
                    {
                        name: 'experienceMonths',
                        placeholder: 'month',
                        format: wholeNumber,
                        disableBrowserSuggestions: true,
                        defaultValue: () => (record?.experienceMonths == null ? '' : String(record.experienceMonths)),
                        onblur: (value, target) => {
                            const form = target?.form;
                            const yearsInput = form?.elements?.experienceYears;
                            const rolled = rollExperienceMonths(yearsInput?.value, value);
                            if (!rolled) return value;
                            if (yearsInput) yearsInput.value = String(rolled.years);
                            return String(rolled.months);
                        },
                    },
                ],
            },
            ...(record ? editOnlyColumns(record, rows) : []),
            {
                label: 'Note',
                name: 'note',
                type: 'textArea',
                rowCount: 3,
                defaultValue: () => record?.note || '',
            },
            {
                label: 'Files',
                name: 'files',
                type: 'dropBox',
                fileType: 'any',
                multiple: true,
                file: null,
                files: (record?.files || []).map((file, index) => ({
                    ...file,
                    name: file.fileName,
                    lastModified: index + 1,
                })),
            },
        ],
        customCheckBeforeSending: async (data) => {
            const name = data.name && String(data.name).trim();
            const phone = data.phoneNumber && String(data.phoneNumber).trim();
            const email = data.email && String(data.email).trim();
            if (!name && !phone && !email) {
                addNotification({ message: 'Name, phone, or email is required', type: 'alert' });
                return { cancelSubmission: true };
            }
            if (phone === 'INVALID INPUT' || email === 'INVALID INPUT') {
                addNotification({ message: 'Check the phone or email', type: 'alert' });
                return { cancelSubmission: true };
            }
            if (data.experienceMonths != null && (data.experienceMonths < 0 || data.experienceMonths > 11)) {
                addNotification({ message: 'Experience months must be between 0 and 11', type: 'alert' });
                return { cancelSubmission: true };
            }
            if (data.experienceYears != null && data.experienceYears < 0) {
                addNotification({ message: 'Experience years cannot be negative', type: 'alert' });
                return { cancelSubmission: true };
            }
            if (data.paymentAmount != null && !data.paymentType) {
                addNotification({ message: 'Payment type is required when an amount is set', type: 'alert' });
                return { cancelSubmission: true };
            }
            return null;
        },
        buildRequestBody: (data, file, files) => {
            const selectedFiles = files || [];
            conf._originalFiles = record?.files || [];
            conf._selectedFiles = selectedFiles;
            const years = data.experienceYears;
            const months = data.experienceMonths;
            const hasExperience = years != null || months != null;
            const body = {
                driverType: data.driverType || 'Company',
                name: data.name || null,
                phoneNumber: data.phoneNumber || null,
                email: data.email || null,
                reference: data.reference || null,
                experienceYears: hasExperience ? (years || 0) : null,
                experienceMonths: hasExperience ? (months || 0) : null,
                note: data.note || null,
                paymentType: data.paymentType || null,
                paymentAmount: data.paymentAmount == null ? null : data.paymentAmount,
            };
            if (record) {
                body.status = data.status || record.status;
                body.insuranceStatus = data.insuranceStatus || record.insuranceStatus;
                body.comingDate = data.comingDate || null;
                conf._companyId = Object.prototype.hasOwnProperty.call(data, 'companyId')
                    ? (data.companyId == null ? '' : String(data.companyId))
                    : null;
                conf._partnerId = Object.prototype.hasOwnProperty.call(data, 'partnerId')
                    ? (data.partnerId == null ? '' : String(data.partnerId))
                    : null;
            }
            return body;
        },
    };
    return {
        configuration: conf,
        postSuccessAction: (saved) => {
            const selected = conf._selectedFiles || [];
            const original = conf._originalFiles || [];
            const removed = original
                .filter((item) => item?.filePath && !selected.some((file) => file.filePath === item.filePath))
                .map((item) => item.filePath);
            const added = selected.filter((item) => item instanceof File);
            const finish = async () => {
                if (record && conf._companyId != null && accessibleCompanies().length > 1) {
                    const nextCompany = conf._companyId === '' ? null : Number(conf._companyId);
                    if (nextCompany !== (record.companyId ?? null)) {
                        await hiringApi.assignCompany(saved.id, nextCompany);
                    }
                }
                if (record && conf._partnerId != null) {
                    const previousPartner = record.partnerId == null ? '' : String(record.partnerId);
                    if (conf._partnerId !== previousPartner) {
                        if (previousPartner) await hiringApi.unlinkTeam(saved.id);
                        if (conf._partnerId) await hiringApi.linkTeam(saved.id, Number(conf._partnerId));
                    }
                }
                for (const filePath of removed) {
                    await hiringApi.removeFile(saved.id, filePath);
                }
                for (const file of added) {
                    await hiringApi.addFile(saved.id, file);
                }
            };
            finish().then(() => onSaved?.()).catch((error) => {
                addNotification({ message: hiringErrorMessage(error), type: 'alert' });
                onSaved?.();
            });
        },
    };
}
